import { Stack, Theme, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import Logo from '@/components/Icon/Logo';
import { Screen } from '@/components/Layout/Screen';

import { ERROR_CODE } from '@/constants/api';
import { LS_USER_BLOCKED_AT_PATH } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';

import { getAPIErrorCode } from '@/utils/api';
import { PROGRESS_STEPS } from '@/utils/progressSteps';

import { AddAdultDetailForm } from '../components/ApplicationTypeSelection/AdultDetailForm';
import { AddAdultDetailFormType } from '../components/ApplicationTypeSelection/AdultDetailForm/helper';

const AddAdultDetailScreenComponent: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const [_, setUserBlockedPath] = useLocalStorage(LS_USER_BLOCKED_AT_PATH, '');
  const { logo } = useAppSelector(selectConfigsState);

  const handleSubmitSuccess = (data: AddAdultDetailFormType) => {
    navigate(ROUTE.ADD_ADULT_DETAIL_RESULT, {
      replace: true,
      state: { adultDetail: data },
    });
  };

  const handleSubmitError = (error: unknown) => {
    if (getAPIErrorCode(error) === ERROR_CODE.ADULT_UNDER_18) {
      navigate(ROUTE.ADD_ADULT_DETAIL_RESULT_FAILED, {
        replace: true,
      });
      setUserBlockedPath(ROUTE.ADD_ADULT_DETAIL_RESULT_FAILED);
    }
  };

  return (
    <Screen
      header={{
        centerSlot: (
          <ProgressDots
            value={PROGRESS_STEPS.thirdPartyComplianceCheckStep.value}
            total={PROGRESS_STEPS.thirdPartyComplianceCheckStep.total}
          />
        ),
      }}
      bgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <Logo src={logo} alt="Logo" />
      <Stack direction="column" gap={3} flex={1}>
        <Typography variant="h3" textAlign="left" mb={1}>
          {t('page.addAdultDetail.title')}
        </Typography>
        <AddAdultDetailForm
          onSubmitted={handleSubmitSuccess}
          onSubmitError={handleSubmitError}
        />
      </Stack>
    </Screen>
  );
};

export default function AddAdultDetailScreen() {
  return <AddAdultDetailScreenComponent />;
}
